import { useEffect, useRef } from "react";
import { AccompanyingItem as AccompanyingItemType } from "./AccompanyingItem.interface";

interface Props {
    item: AccompanyingItemType;
    isSelected: boolean;
    onSelect: () => void;
    onRemove: () => void;
    isCurrent?: boolean;
    isCartProduct?: boolean;
}

/**
 * Карточка сопутствующего товара
 * @param item - товар
 * @param isSelected - выбран ли товар
 * @param onSelect - функция для выбора товара
 * @param onRemove - функция для удаления товара
 * @returns 
 */
export const AccompanyingItem = ({ item, isSelected, onSelect, onRemove, isCurrent = false, isCartProduct = false }: Props) => {
    const linkRef = useRef<HTMLAnchorElement>(null);
    const checkboxRef = useRef<HTMLButtonElement>(null);

    //Если товар уже в корзине, то выбираем его автоматически
    useEffect(() => {
        if (isCartProduct) {
            onSelect();
        }
    }, []);

    /** Обработка клика по карточке */
    const handleClickByCard = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target !== linkRef.current && e.target !== checkboxRef.current && !isSelected) {
            onSelect();
        }
    };

    return (
        <div key={item.id}
            onClick={handleClickByCard}
            className="flex items-start gap-2 p-2 bg-white rounded-lg border border-gray-200 hover:border-orange-300 hover:shadow-md transition-all cursor-pointer">
            <button type="button"
                ref={checkboxRef}
                role="checkbox"
                aria-checked="true"
                data-state={isSelected ? 'checked' : 'unchecked'}
                value="on"
                onClick={() => isSelected ? onRemove() : onSelect()}
                className="peer h-4 w-4 shrink-0 rounded-sm border border-primary ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground text-orange-500 mt-0.5 flex-shrink-0 cursor-default">
                {isSelected && <span data-state="checked" className="flex items-center justify-center text-current" style={{ pointerEvents: 'none' }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-check h-4 w-4">
                        <path d="M20 6 9 17l-5-5"></path>
                    </svg>
                </span>}
            </button>
            <div className="flex-1 min-w-0 flex flex-col gap-1 h-full">
                <div className="flex gap-1">
                    {isCurrent &&
                        <span className="text-xs text-orange-600 font-medium bg-orange-100 px-1 py-0.5 rounded-full w-fit">
                            Сейчас просматриваете
                        </span>}
                    {isCartProduct &&
                        <span className="text-xs text-orange-600 font-medium bg-orange-100 px-1 py-0.5 rounded-full w-fit">
                            В корзине
                        </span>}
                </div>
                <a href={`/spares/${item.slug}/${item.article}`}
                    ref={linkRef}
                    className="text-xs font-medium cursor-pointer transition-colors leading-tight text-gray-900 hover:text-orange-600">
                    {item.name}
                </a>
                <p className="text-xs text-gray-500">
                    Бренд: {item.brand}
                </p>
                <p className="text-xs text-gray-500 mb-1">
                    Артикул: {item.article}
                </p>
                <div className="flex items-center justify-between mt-auto mb-1">
                    <p className="text-sm font-bold text-gray-900">{item.price} ₽</p>
                    <div className="flex items-center gap-1 bg-gray-100 px-1 py-0.5 rounded-md">
                        <p className="text-xs text-gray-600">Кол-во:</p>
                        <p className="text-xs font-medium text-gray-900">{item.stock}</p>
                    </div>
                </div>
            </div>
        </div>
    );
};